import React, { useState, useEffect, useCallback } from "react";
import { Plus, RefreshCcw, Eye, Play, Archive, RotateCw } from "lucide-react";
import { Link } from "react-router-dom";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { EnvStatusBadge, EnvTypeBadge } from "./environments/EnvBadges";
import EnvironmentFormDialog from "./environments/EnvironmentFormDialog";
import DeploymentSubNav from "./deployments/DeploymentSubNav";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function PlatformEnvironments() {
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [envs, ts] = await Promise.all([
        api.get("/platform/environments"),
        api.get("/platform/tenants"),
      ]);
      setItems(envs.data.items || []);
      setTenants(ts.data.items || []);
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const action = async (id, verb) => {
    setBusyId(id + verb);
    try {
      await api.post(`/platform/environments/${id}/${verb}`, {});
      toast.success(`Config ${verb} berhasil.`);
      load();
    } catch (e) { notifyError(e, `Gagal ${verb}`); }
    finally { setBusyId(null); }
  };

  const tenantOf = (id) => tenants.find((t) => t.tenant_id === id);

  return (
    <PlatformLayout title="Environment Configs">
      <div data-testid="platform-environments-page">
        <DeploymentSubNav />

        {/* Toolbar */}
        <div className="flex items-center gap-3 mb-4">
          <div className="text-xs text-white/50">
            Menampilkan {items.length} environment config
          </div>
          <Button onClick={load} variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/10 ml-auto">
            <RefreshCcw size={14} className="mr-1" /> Refresh
          </Button>
          <Button onClick={() => setDialogOpen(true)} className="bg-white text-black hover:bg-white/90" data-testid="btn-generate-env">
            <Plus size={14} className="mr-1" /> Generate Config
          </Button>
        </div>

        {loading ? (
          <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
        ) : items.length === 0 ? (
          <div className="text-white/40 text-sm p-8 rounded-2xl border border-white/10 text-center">
            Belum ada environment config. Klik <strong className="text-white">+ Generate Config</strong> untuk mulai.
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-white/5 text-[10px] uppercase tracking-wider text-white/40 font-bold">
              <div className="col-span-3">Tenant</div>
              <div className="col-span-2">Environment</div>
              <div className="col-span-3">App URL</div>
              <div className="col-span-1">Version</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            {items.map((cfg) => {
              const t = tenantOf(cfg.tenant_id);
              const canActivate = ["GENERATED"].includes(cfg.status);
              const canArchive = ["GENERATED", "ACTIVE"].includes(cfg.status);
              return (
                <div key={cfg.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-t border-white/5 items-center"
                  data-testid={`env-row-${cfg.id}`}>
                  <div className="col-span-3 min-w-0">
                    <Link to={`/platform/deployments/environments/${cfg.id}`}
                      className="text-sm font-bold text-white hover:text-emerald-300 truncate block"
                      data-testid={`env-tenant-link-${cfg.id}`}>
                      {t?.tenant_name || cfg.tenant_slug}
                    </Link>
                    <div className="text-[10px] text-white/40 font-mono truncate">{cfg.tenant_slug}</div>
                  </div>
                  <div className="col-span-2"><EnvTypeBadge environment={cfg.environment} size="sm" /></div>
                  <div className="col-span-3 min-w-0 text-xs text-white/70 font-mono truncate">{cfg.app_url}</div>
                  <div className="col-span-1 text-xs text-white/70 font-mono">v{cfg.config_version}</div>
                  <div className="col-span-1"><EnvStatusBadge status={cfg.status} size="sm" /></div>
                  <div className="col-span-2 flex gap-1 justify-end">
                    <Link to={`/platform/deployments/environments/${cfg.id}`}
                      className="w-7 h-7 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10"
                      title="View" data-testid={`env-view-${cfg.id}`}>
                      <Eye size={12} />
                    </Link>
                    {canActivate && (
                      <button onClick={() => action(cfg.id, "activate")} disabled={busyId === cfg.id + "activate"}
                        className="w-7 h-7 rounded-lg grid place-items-center text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
                        title="Activate" data-testid={`env-activate-${cfg.id}`}>
                        <Play size={12} />
                      </button>
                    )}
                    <button onClick={() => action(cfg.id, "regenerate")} disabled={busyId === cfg.id + "regenerate"}
                      className="w-7 h-7 rounded-lg grid place-items-center text-sky-400 hover:bg-sky-500/10 disabled:opacity-50"
                      title="Regenerate (new version)" data-testid={`env-regenerate-${cfg.id}`}>
                      <RotateCw size={12} />
                    </button>
                    {canArchive && (
                      <button onClick={() => action(cfg.id, "archive")} disabled={busyId === cfg.id + "archive"}
                        className="w-7 h-7 rounded-lg grid place-items-center text-neutral-400 hover:bg-neutral-500/10 disabled:opacity-50"
                        title="Archive" data-testid={`env-archive-${cfg.id}`}>
                        <Archive size={12} />
                      </button>
                    )}
                  </div>
                  <div className="col-span-12 md:col-span-12 md:hidden text-[10px] text-white/40 mt-1">Generated {fmt(cfg.generated_at || cfg.created_at)}</div>
                </div>
              );
            })}
          </div>
        )}

        <EnvironmentFormDialog tenants={tenants} open={dialogOpen} onClose={() => setDialogOpen(false)} onGenerated={load} />
      </div>
    </PlatformLayout>
  );
}
